import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { PlantBadge, plantAlt, Section, Heading, Spinner } from "../components/Bits";
import PageHero from "../components/PageHero";
import { useSettings } from "../lib/settings";
import { monthPhoto } from "../lib/siteTexts";

const MONTHS = [
  { num: 1, name: "Januar" }, { num: 2, name: "Februar" }, { num: 3, name: "März" },
  { num: 4, name: "April" }, { num: 5, name: "Mai" }, { num: 6, name: "Juni" },
  { num: 7, name: "Juli" }, { num: 8, name: "August" }, { num: 9, name: "September" },
  { num: 10, name: "Oktober" }, { num: 11, name: "November" }, { num: 12, name: "Dezember" },
];

function MonthMood({ month, count }) {
  const { url, alt, mood } = monthPhoto(useSettings(), month);
  const name = MONTHS[month - 1].name;
  return (
    <div className="relative rounded-2xl overflow-hidden mb-8 aspect-[16/7] md:aspect-[21/6]" data-testid={`calendar-month-photo-${month}`}>
      <img key={url} src={url} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover animate-[fadeIn_0.5s_ease]" style={{ objectPosition: "center 45%" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(28,46,31,0.82) 0%, rgba(28,46,31,0.35) 55%, rgba(28,46,31,0) 100%)" }} />
      <div className="relative h-full flex flex-col justify-end p-6 md:p-10" style={{ color: "#F5EFE3" }}>
        <div className="text-xs uppercase tracking-[0.2em] opacity-80 mb-1" data-testid="calendar-month-mood">{mood}</div>
        <div className="font-display text-3xl md:text-5xl">{name}</div>
        <div className="text-sm opacity-85 mt-1" data-testid="calendar-month-count">{count} {count === 1 ? "Pflanze" : "Pflanzen"} in der Saison</div>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    api.get("/calendar").then((r) => setData(r.data || {})).finally(() => setLoading(false));
  }, []);

  return (
    <Section>
      <PageHero slug="season" variant="soft"
        defaultOverline="Saisonkalender"
        defaultTitle="Was wächst wann?"
        defaultSubtitle="Klicke auf einen Monat – wir zeigen dir, welche Wildpflanzen in Luxemburg gerade verfügbar sind."
      />
      <div className="flex flex-wrap gap-2 mb-10" data-testid="calendar-months">
        {MONTHS.map((m) => (
          <button
            key={m.num}
            onClick={() => setActive(m.num)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${
              active === m.num ? "bg-[var(--wp-primary)] text-[var(--wp-cream)] border-[var(--wp-primary)]" : "bg-white hover:bg-[var(--wp-cream)]"
            }`}
            style={active !== m.num ? { borderColor: "var(--wp-border)" } : {}}
            data-testid={`month-${m.num}`}
          >
            {m.name}
          </button>
        ))}
      </div>

      <MonthMood month={active} count={(data[active] || []).length} />

      {loading ? (
        <Spinner />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="calendar-plants">
          {(data[active] || []).length === 0 && (
            <div className="col-span-full text-sm opacity-70">Keine Pflanzen für diesen Monat in der Datenbank.</div>
          )}
          {(data[active] || []).map((p) => (
            <Link key={p.id} to={`/plants/${p.slug}`} className="plant-card flex items-center gap-3 p-3">
              <img src={p.season_image_url || p.image_url} alt={plantAlt(p)} loading="lazy" className="w-16 h-16 object-cover rounded-lg" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.name_de}</div>
                <div className="mt-1"><PlantBadge category={p.category} /></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Section>
  );
}
